import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Plus, Trash2 } from 'lucide-react';
import ClientIcon from '@/svg-icons/client';

const DocuSignSigners = ({ formData, onChange, name, errorSchema, formContext }) => {
  const [signers, setSigners] = useState(() =>
    formData && Array.isArray(formData) && formData.length > 0
      ? [...formData]
      : [{ name: '', email: '', status: 'created' }]
  );

  const isEditing = useCallback(() => {
    return !!formData && formData.length > 0 && formData.some(s => s.id);
  }, [formData]);

  const [editingState, setEditingState] = useState(isEditing());
  const [focusedInput, setFocusedInput] = useState(null);

  useEffect(() => {
    setEditingState(isEditing());
    if (formData && Array.isArray(formData) && formData.length > 0) {
      setSigners([...formData]);
    } else {
      const defaultSigners = [{ name: '', email: '', status: 'created' }];
      setSigners(defaultSigners);
      if (!formData || formData.length === 0) {
        onChange(defaultSigners);
      }
    }
  }, [formData, isEditing, onChange]);

  const handleSignersChange = useCallback((updatedSigners) => {
    setSigners(updatedSigners);
    onChange(updatedSigners);
  }, [onChange]);

  const handleChange = (propertyName, index) => (event) => {
    const updatedSigners = [...signers];
    updatedSigners[index] = {
      ...updatedSigners[index],
      [propertyName]: event.target.value,
    };
    if (updatedSigners[index].id) {
      updatedSigners[index].status = 'updated';
    } else if (!updatedSigners[index].status) {
      updatedSigners[index].status = 'created';
    }
    handleSignersChange(updatedSigners);
  };

  const handleAddSigner = () => {
    const updatedSigners = [...signers, { name: '', email: '', status: 'created' }];
    handleSignersChange(updatedSigners);
  };

  const handleRemoveSigner = (index) => () => {
    const updatedSigners = [...signers];
    if (signers.length === 1 && !updatedSigners[index].id) {
      updatedSigners[0].name = '';
      updatedSigners[0].email = '';
      updatedSigners[0].status = undefined;
    } else if (updatedSigners[index].id !== undefined) {
      updatedSigners[index].status = 'deleted';
    } else {
      updatedSigners.splice(index, 1);
    }
    handleSignersChange(updatedSigners);
  };

  const isErrorName = (index) => {
    if (errorSchema?.[index]) {
      return signers[index]?.name === '';
    }
    return false;
  };

  const isErrorEmail = (index) => {
    const check = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (errorSchema?.[index]) {
      return !check.test(String(signers[index]?.email || ''));
    }
    return false;
  };

  const disableDelete = () => {
    const activeSigners = signers.filter(s => s.status !== 'deleted');
    return (
      activeSigners.length === 1 &&
      (!activeSigners[0].name || activeSigners[0].name === '') &&
      (!activeSigners[0].email || activeSigners[0].email === '')
    );
  };

  const visibleSigners = signers.filter(s => s.status !== 'deleted');

  return (
    <div className="flex flex-row w-full pr-2 font-['Open_Sans']">
      {/* Left column: icon */}
      <ClientIcon
        viewBox="0 0 34.016 34.016"
        width="20"
        height="20"
        style={{ flexShrink: 0 }}
      />

      {/* Right column: all signers content */}
      <div className="flex-1 pl-2">
        {visibleSigners.map((signer, index) => {
          const nameError = isErrorName(index);
          const emailError = isErrorEmail(index);
          const isLocked =
            signer.status &&
            signer.status !== 'pending' &&
            signer.status !== 'created' &&
            signer.status !== 'updated';

          return (
            <div key={signer.id || index}>
              {/* Header: title + remove button */}
              <div className="flex items-center justify-between mb-[0.6rem]">
                <p className="mb-0 font-bold text-[calc(10px+0.4rem)] font-['Open_Sans']">
                  <FormattedMessage id={name} /> {index + 1}
                </p>
                <button
                  type="button"
                  disabled={disableDelete() || editingState}
                  onClick={handleRemoveSigner(index)}
                  className={`w-6 h-6 rounded-full flex items-center justify-center border-none transition-all ${
                    disableDelete() || editingState
                      ? 'bg-white text-[#cce2f5] cursor-default'
                      : 'bg-white text-[#5882c1] cursor-pointer hover:brightness-125'
                  }`}
                  style={{ boxShadow: 'none', outline: 'none' }}
                  data-cy={`${index}-signer-remove`}
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Name field */}
              <div className="mb-2">
                <span
                  className="block font-['Open_Sans']"
                  style={{ marginTop: '0.6rem', lineHeight: '1.2rem', fontSize: 'calc(6px + .2rem)' }}
                >
                  <FormattedMessage id="recipients.Signer.name" />*
                </span>
                <input
                  type="text"
                  value={signer.name || ''}
                  onChange={handleChange('name', index)}
                  onFocus={() => setFocusedInput(`name-${index}`)}
                  onBlur={() => setFocusedInput(null)}
                  disabled={isLocked}
                  className="block w-full font-['Open_Sans']"
                  style={{
                    height: 'auto',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: `1px solid ${nameError ? 'red' : focusedInput === `name-${index}` ? '#80bdff' : '#ced4da'}`,
                    fontSize: 'calc(6px + .6rem)',
                    fontWeight: 'normal',
                    color: '#22324d',
                    width: '100%',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                  data-cy={`${index}-signer-name`}
                />
                {nameError && (
                  <ul className="mt-1 font-['Open_Sans'] font-normal text-[#dc3545] list-disc" style={{ fontSize: 'calc(6px + 0.3vw)' }}>
                    <li>
                      <FormattedMessage id="requiredField" />
                    </li>
                  </ul>
                )}
              </div>

              {/* Email field */}
              <div className="mb-4">
                <span
                  className="block font-['Open_Sans']"
                  style={{ marginTop: '0.6rem', lineHeight: '1.2rem', fontSize: 'calc(6px + .2rem)' }}
                >
                  <FormattedMessage id="recipients.email" />*
                </span>
                <input
                  type="email"
                  value={signer.email || ''}
                  onChange={handleChange('email', index)}
                  onFocus={() => setFocusedInput(`email-${index}`)}
                  onBlur={() => setFocusedInput(null)}
                  disabled={isLocked}
                  className="block w-full font-['Open_Sans']"
                  style={{
                    height: 'auto',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: `1px solid ${emailError ? 'red' : focusedInput === `email-${index}` ? '#80bdff' : '#ced4da'}`,
                    fontSize: 'calc(6px + .6rem)',
                    fontWeight: 'normal',
                    color: '#22324d',
                    width: '100%',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                  data-cy={`${index}-signer-email`}
                />
                {emailError && (
                  <ul className="mt-1 font-['Open_Sans'] font-normal text-[#dc3545] list-disc" style={{ fontSize: 'calc(6px + 0.3vw)' }}>
                    <li>
                      <FormattedMessage id="requiredEmail" />
                    </li>
                  </ul>
                )}
              </div>
            </div>
          );
        })}

        {/* Add signer button — centered circle on horizontal line */}
        {!editingState && (
          <div
            className="flex items-center justify-center w-full my-8"
            style={{
              paddingLeft: 'calc(.5rem + 1.5rem)',
              paddingRight: 'calc(15px + 1.5rem)',
            }}
          >
            {/* Left line */}
            <div className="flex-1 h-[1px] bg-[#e9e9e9]" />
            <button
              type="button"
              onClick={handleAddSigner}
              className="flex items-center justify-center bg-[#5882c1] text-white cursor-pointer hover:bg-[#4771b0] transition-colors"
              style={{
                width: '24px',
                height: '24px',
                minWidth: '24px',
                minHeight: '24px',
                borderRadius: '50%',
                border: 'none',
                padding: 0,
                boxShadow: 'none',
                outline: 'none',
              }}
              data-cy="add-signer"
            >
              <Plus size={16} strokeWidth={2.5} />
            </button>
            {/* Right line */}
            <div className="flex-1 h-[1px] bg-[#e9e9e9]" />
          </div>
        )}
      </div>
    </div>
  );
};

DocuSignSigners.propTypes = {
  formData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string,
    })
  ),
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  errorSchema: PropTypes.object,
  formContext: PropTypes.object,
};

DocuSignSigners.defaultProps = {
  formData: null,
};

export default injectIntl(DocuSignSigners);
